import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Reservation, ReservationClash, Table } from '../../interfaces';
import {
    CLOSED_RESERVATION_STATUSES,
    LateStatus,
    RESERVATION_STATUS_META,
    UseReservationsResult,
    minutesOfTime,
    plannedTurnMinutes,
    serviceWindowsOn,
    toDateKey,
} from '../../hooks/useReservations';
import { ReservationSaveResult } from '../../services/ReservationService';
import DateJumper from './DateJumper';
import './ServiceTimeline.css';

/** What a busy table is doing when no booking is behind it (a walk-in). */
export interface Occupancy {
    /** Minutes past midnight the table was opened. */
    startMin: number;
    /** Minutes past midnight it is planned to free up. */
    endMin: number;
    covers: number;
    /** Already past its planned turn. */
    over: boolean;
}

interface Props {
    resv: UseReservationsResult;
    /** Tables on the floor being viewed. */
    tables: Table[];
    now: number;
    /** Occupancy of a table sat without a booking, or null when it is free. */
    occupancyFor: (t: Table) => Occupancy | null;
    lateFor: (r: Reservation) => LateStatus | null;
    /** reservationId → the other floor its tables are on. */
    otherFloorByReservationId: Record<string, string>;
    onOpenBooking: (r: Reservation) => void;
    onAssign: (r: Reservation) => void;
    /** Tapping an empty stretch of a lane: book that table at that time. */
    onCreateAt: (time: string, tableId?: string) => void;
    /** Dropping a dragged block: a new time, a new table, or both. */
    onMove: (
        r: Reservation,
        changes: { time?: string; tableIds?: string[] },
        allowOverlap?: boolean,
    ) => Promise<ReservationSaveResult>;
    /** Set while a booking is being given tables; lanes become the picker. */
    assigning: Reservation | null;
    assignPicks: string[];
    onPickTable: (t: Table) => void;
    canCreate: boolean;
    /** Whether this staff member may move a booking by dragging it. */
    canEdit: boolean;
}

/**
 * Narrow enough that a whole evening fits a tablet, wide enough that a 30-minute
 * block still reads. Below the minimum the grid scrolls rather than compressing,
 * the same bargain the floor plan makes.
 */
const MIN_HOUR_W = 92;
const MAX_HOUR_W = 220;

/** Nothing booked, nothing open: a plausible evening rather than an empty day. */
const FALLBACK_SPAN = { startMin: 12 * 60, endMin: 23 * 60 };
const MIN_SPAN_MINUTES = 6 * 60;

/**
 * How far outside the service the clock may still pull the grid open. Within it,
 * "now" is worth seeing — the empty hour before the first booking is exactly
 * where a walk-in goes. Beyond it the clock is nowhere near service and would
 * only stretch the axis across hours nobody is working: at one in the morning,
 * counting `now` turned an evening's bookings into a 24-hour scroll.
 */
const NOW_REACH_MINUTES = 3 * 60;

const icons = {
    prev: <svg viewBox='0 0 24 24' fill='none'><path d='m15 18-6-6 6-6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    next: <svg viewBox='0 0 24 24' fill='none'><path d='m9 6 6 6-6 6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    people: <svg viewBox='0 0 24 24' width='11' height='11' fill='none'><path d='M16 19v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V19' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' /><circle cx='9' cy='7' r='3.2' stroke='currentColor' strokeWidth='1.9' /><path d='M22 19v-1.4a4 4 0 0 0-3-3.8M17.5 4.3a3.2 3.2 0 0 1 0 5.9' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' /></svg>,
};

const clock = (min: number): string => {
    const wrapped = ((min % 1440) + 1440) % 1440;
    return `${`${Math.floor(wrapped / 60)}`.padStart(2, '0')}:${`${wrapped % 60}`.padStart(2, '0')}`;
};

/** T2 before T10, which a plain string sort will not do. */
const byTableNo = (a: Table, b: Table): number => (
    (a.tableNo || '').localeCompare(b.tableNo || '', undefined, { numeric: true })
);

const guestLabel = (r: Reservation): string => (
    `${r.guest?.firstName || ''} ${r.guest?.lastName || ''}`.trim() || r.code
);

interface Placeable {
    startMin: number;
    endMin: number;
}

/**
 * Stack anything that overlaps into sub-lanes and say how many were needed.
 *
 * Two sittings on one table is a thing a host is allowed to do deliberately, and
 * the grid is where they would notice having done it by accident — so a clash
 * grows the row and shows both, rather than letting one hide behind the other.
 * The queue of bookings with no table yet leans on this too: half a dozen of them
 * at eight o'clock is normal, and they all have to be readable.
 */
const packLanes = <T extends Placeable>(items: T[]): { packed: (T & { lane: number })[]; lanes: number } => {
    const ends: number[] = [];
    const packed = [...items]
        .sort((a, b) => a.startMin - b.startMin)
        .map(item => {
            const found = ends.findIndex(end => end <= item.startMin);
            const lane = found === -1 ? ends.length : found;
            ends[lane] = item.endMin;
            return { ...item, lane };
        });
    return { packed, lanes: Math.max(1, ends.length) };
};

/** A block's slot inside its row, in whichever sub-lane it was packed into. */
const laneBox = (lane: number) => ({
    top: `calc(var(--tline-row-h) * ${lane} + 3px)`,
    height: 'calc(var(--tline-row-h) - 6px)',
});

/**
 * Only a booking that has not walked in yet can be dragged. A seated party is in
 * the room — moving them is a decision about furniture, not a booking edit — and a
 * completed one is history. `arrived` is left out too: they are at the door, and
 * giving them a different table is what the assign flow is for.
 */
const MOVABLE_STATUSES = ['requested', 'confirmed'];

/**
 * A finger has to be able to scroll a grid that is mostly blocks, so on touch a
 * block is picked up by holding it rather than by moving it: this is how long, and
 * this is how far a touch may stray in that time before it is read as a scroll.
 * A mouse has no such problem and starts dragging as soon as it means to.
 */
const HOLD_MS = 350;
const HOLD_SLOP_PX = 8;
const MOUSE_SLOP_PX = 4;

/** What is being dragged, and where it would land if dropped now. */
interface DragState {
    booking: Reservation;
    /** Where in the block it was grabbed, so it doesn't jump to the fingertip. */
    grabOffsetPx: number;
    /** Held picks up in place; the preview only appears once it has moved. */
    moved: boolean;
    startMin: number;
    /** The row under the pointer, or undefined when it is not over one. */
    tableId?: string;
    tableNo?: string;
    /** Pixels from the top of the rows box to the target row. */
    rowTop: number;
    /** A join keeps its tables: dragging one changes the time only. */
    timeOnly: boolean;
}

/** A move the server refused, kept so it can be named and then insisted upon. */
interface RefusedMove {
    booking: Reservation;
    changes: { time?: string; tableIds?: string[] };
    clashes: ReservationClash[];
}

/**
 * The service as a table × time grid.
 *
 * The rail answers "what is coming next" and the floor plan answers "where", but
 * neither answers the question a host is asked on the phone twenty times a night:
 * can you do eight o'clock. That is a question about a shape — which tables are
 * busy when, and how wide the gaps between them are — and a shape is the one
 * thing a list cannot draw.
 *
 * Rows are the tables of the floor on screen, because tables belong to floors and
 * every other view is already scoped that way. Columns are the hours the day
 * actually uses rather than a fixed 00:00–24:00, so the grid opens on the service
 * instead of on the small hours.
 */
const ServiceTimeline: React.FC<Props> = ({
    resv,
    tables,
    now,
    occupancyFor,
    lateFor,
    otherFloorByReservationId,
    onOpenBooking,
    onAssign,
    onCreateAt,
    onMove,
    assigning,
    assignPicks,
    onPickTable,
    canCreate,
    canEdit,
}) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const rowsRef = useRef<HTMLDivElement>(null);
    const cornerRef = useRef<HTMLDivElement>(null);
    const dateBtnRef = useRef<HTMLButtonElement>(null);
    const [hourWidth, setHourWidth] = useState(MIN_HOUR_W);
    const [jumperOpen, setJumperOpen] = useState(false);
    const [drag, setDrag] = useState<DragState | null>(null);
    const [refused, setRefused] = useState<RefusedMove | null>(null);

    const rows = useMemo(() => [...tables].sort(byTableNo), [tables]);
    const slotMinutes = resv.settings?.slotIntervalMinutes || 15;
    const bufferMinutes = resv.settings?.bufferMinutes ?? 0;

    /** Bookings that hold a table for a stretch of the day, so can be drawn. */
    const drawable = useMemo(() => resv.onDate.filter(r => (
        r.status !== 'waitlist' && r.status !== 'cancelled' && r.status !== 'noShow'
    )), [resv.onDate]);

    const spanOf = useCallback((r: Reservation) => {
        const startMin = minutesOfTime(r.time);
        const minutes = r.durationMinutes || plannedTurnMinutes(resv.settings, r.partySize);
        return { startMin, endMin: startMin + minutes };
    }, [resv.settings]);

    const nowMin = useMemo(() => {
        const d = new Date(now);
        return d.getHours() * 60 + d.getMinutes();
    }, [now]);

    /**
     * The hours worth drawing: everything booked or sat, the sittings if the venue
     * uses them, and the clock when it is anywhere near them. Rounded out to whole
     * hours so the axis has clean labels, and never narrower than six hours so a
     * day with one lunch booking doesn't render as a two-hour sliver.
     */
    const span = useMemo(() => {
        const marks: number[] = [];
        drawable.forEach(r => {
            const { startMin, endMin } = spanOf(r);
            marks.push(startMin, endMin + bufferMinutes);
        });
        if (serviceWindowsOn(resv.settings)) {
            const dow = new Date(`${resv.dateKey}T12:00:00`).getDay();
            (resv.settings?.services || [])
                .filter(s => s.isActive && s.daysOfWeek.includes(dow))
                .forEach(s => marks.push(minutesOfTime(s.startTime), minutesOfTime(s.endTime)));
        }
        if (resv.isToday) {
            rows.forEach(t => {
                const busy = occupancyFor(t);
                if (busy) marks.push(busy.startMin, busy.endMin);
            });
        }
        if (!marks.length) return FALLBACK_SPAN;

        let lo = Math.min(...marks);
        let hi = Math.max(...marks);
        if (resv.isToday && nowMin >= lo - NOW_REACH_MINUTES && nowMin <= hi + NOW_REACH_MINUTES) {
            lo = Math.min(lo, nowMin);
            hi = Math.max(hi, nowMin);
        }

        const startMin = Math.max(0, Math.floor(lo / 60) * 60);
        const endMin = Math.max(Math.ceil(hi / 60) * 60, startMin + MIN_SPAN_MINUTES);
        return { startMin, endMin };
    }, [drawable, spanOf, bufferMinutes, resv.settings, resv.dateKey, resv.isToday, nowMin, rows, occupancyFor]);

    /** Whether the clock falls inside the hours drawn — off-service, it doesn't. */
    const nowInSpan = resv.isToday && nowMin >= span.startMin && nowMin <= span.endMin;
    /** Shared by the line through the rows and the clock pill in the header. */
    const nowLeft = `calc(var(--tline-gutter-w) + ${(nowMin - span.startMin) * (hourWidth / 60)}px)`;
    const hours = Math.max(1, Math.round((span.endMin - span.startMin) / 60));
    const pxPerMin = hourWidth / 60;
    const laneWidth = hours * hourWidth;

    /** Fit the span to the space when it will fit; scroll when it won't. */
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return undefined;
        const measure = () => {
            const gutter = cornerRef.current?.offsetWidth ?? 0;
            const avail = el.clientWidth - gutter;
            setHourWidth(Math.max(MIN_HOUR_W, Math.min(MAX_HOUR_W, Math.floor(avail / hours))));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [hours]);

    const scrollToNow = useCallback(() => {
        const el = viewportRef.current;
        if (!el) return;
        const gutter = cornerRef.current?.offsetWidth ?? 0;
        const x = (nowMin - span.startMin) * pxPerMin;
        // A third in, so what is about to happen is on screen with it.
        el.scrollTo({ left: Math.max(0, x - (el.clientWidth - gutter) / 3), behavior: 'smooth' });
    }, [nowMin, span.startMin, pxPerMin]);

    /** Open on the live end of today rather than on lunch nobody is serving. */
    const openedOn = useRef('');
    useEffect(() => {
        if (!nowInSpan || openedOn.current === resv.dateKey) return;
        openedOn.current = resv.dateKey;
        const el = viewportRef.current;
        if (!el) return;
        const gutter = cornerRef.current?.offsetWidth ?? 0;
        const x = (nowMin - span.startMin) * pxPerMin;
        el.scrollLeft = Math.max(0, x - (el.clientWidth - gutter) / 3);
    }, [nowInSpan, resv.dateKey, nowMin, span.startMin, pxPerMin]);

    const bookingsByTable = useMemo(() => {
        const out: Record<string, Reservation[]> = {};
        drawable.forEach(r => r.tableIds.forEach(id => {
            (out[id] = out[id] || []).push(r);
        }));
        return out;
    }, [drawable]);

    /** Live bookings still waiting for a table — they belong to no row. */
    const unassignedRow = useMemo(() => packLanes(
        drawable
            .filter(r => r.tableIds.length === 0 && !CLOSED_RESERVATION_STATUSES.includes(r.status))
            .map(r => ({ ...spanOf(r), booking: r })),
    ), [drawable, spanOf]);

    /**
     * Bookings sat on another floor. Counted rather than drawn: their tables are
     * not rows here, and a booking that quietly exists nowhere on screen is how a
     * host ends up double-selling a table.
     */
    const elsewhere = useMemo(() => {
        const here = new Set(rows.map(t => t.id));
        const found = drawable.filter(r => r.tableIds.length > 0 && !r.tableIds.some(id => here.has(id)));
        const floorNames = Array.from(new Set(
            found.map(r => otherFloorByReservationId[r.id]).filter(Boolean),
        ));
        return { count: found.length, floors: floorNames.join(' · ') };
    }, [drawable, rows, otherFloorByReservationId]);

    const assignSpan = useMemo(
        () => (assigning ? spanOf(assigning) : null),
        [assigning, spanOf],
    );

    const dateLabel = useMemo(() => {
        const d = new Date(`${resv.dateKey}T12:00:00`);
        if (resv.isToday) return 'Today';
        if (resv.dateKey === toDateKey(new Date(Date.now() + 86400000))) return 'Tomorrow';
        return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    }, [resv.dateKey, resv.isToday]);

    /** Where in the day a tap on a lane landed, snapped to the booking grid. */
    const timeAtClick = (e: React.MouseEvent<HTMLElement>): string => {
        const box = e.currentTarget.getBoundingClientRect();
        const min = span.startMin + (e.clientX - box.left) / pxPerMin;
        return clock(Math.floor(min / slotMinutes) * slotMinutes);
    };

    const laneClick = (e: React.MouseEvent<HTMLElement>, table?: Table) => {
        if (assigning) {
            if (table) onPickTable(table);
            return;
        }
        if (!canCreate || (table && !table.isActive)) return;
        onCreateAt(timeAtClick(e), table?.id);
    };

    // ── Dragging a booking to another time or table ──────────────────────────
    /** Set between pointerdown and the pick-up, and cleared by either outcome. */
    const pending = useRef<{
        pointerId: number;
        booking: Reservation;
        startX: number;
        startY: number;
        grabOffsetPx: number;
        touch: boolean;
        timer?: number;
    } | null>(null);
    /** So the click that follows a drag doesn't also open the booking. */
    const dragged = useRef(false);

    const clearPending = () => {
        if (pending.current?.timer) window.clearTimeout(pending.current.timer);
        pending.current = null;
    };

    /** The row under the pointer and where it sits, or null when over none. */
    const targetAt = (clientX: number, clientY: number) => {
        const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
        const row = el?.closest('.tline-row') as HTMLElement | null;
        const box = rowsRef.current;
        if (!row || !box || row.dataset.off === 'true') return null;
        return {
            tableId: row.dataset.tableId || undefined,
            tableNo: row.dataset.tableNo || undefined,
            rowTop: row.getBoundingClientRect().top - box.getBoundingClientRect().top,
        };
    };

    /** Where the block's left edge would land, snapped to the booking grid. */
    const timeAtDrag = (clientX: number, grabOffsetPx: number): number => {
        const lane = rowsRef.current?.querySelector('.tline-row__lane') as HTMLElement | null;
        if (!lane) return span.startMin;
        const min = span.startMin + (clientX - grabOffsetPx - lane.getBoundingClientRect().left) / pxPerMin;
        const snapped = Math.round(min / slotMinutes) * slotMinutes;
        return Math.max(0, snapped);
    };

    /**
     * Take the block in hand. `moved` is false for the hold that picks one up in
     * place and true for the mouse, which only ever lifts because it has already
     * dragged — so one mouse move is enough to both lift and place it.
     */
    const lift = (clientX: number, clientY: number, moved = false) => {
        const p = pending.current;
        if (!p) return;
        if (p.timer) window.clearTimeout(p.timer);
        const target = targetAt(clientX, clientY);
        setDrag({
            booking: p.booking,
            grabOffsetPx: p.grabOffsetPx,
            moved,
            startMin: moved ? timeAtDrag(clientX, p.grabOffsetPx) : minutesOfTime(p.booking.time),
            tableId: target?.tableId,
            tableNo: target?.tableNo,
            rowTop: target?.rowTop ?? 0,
            timeOnly: p.booking.tableIds.length > 1,
        });
    };

    const blockPointerDown = (e: React.PointerEvent<HTMLButtonElement>, r: Reservation) => {
        if (!canEdit || assigning || refused || !MOVABLE_STATUSES.includes(r.status)) return;
        const touch = e.pointerType !== 'mouse';
        const el = e.currentTarget;
        // Keeps the moves coming after the pointer has left the block, which it does
        // immediately. Not fatal if the browser refuses: the block still receives
        // them while the pointer is over it.
        try {
            el.setPointerCapture(e.pointerId);
        } catch {
            /* no capture available */
        }
        pending.current = {
            pointerId: e.pointerId,
            booking: r,
            startX: e.clientX,
            startY: e.clientY,
            grabOffsetPx: e.clientX - el.getBoundingClientRect().left,
            touch,
        };
        if (touch) {
            const { clientX, clientY } = e;
            pending.current.timer = window.setTimeout(() => lift(clientX, clientY), HOLD_MS);
        }
    };

    const blockPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
        const p = pending.current;
        if (p && !drag) {
            const far = Math.abs(e.clientX - p.startX) > (p.touch ? HOLD_SLOP_PX : MOUSE_SLOP_PX)
                || Math.abs(e.clientY - p.startY) > (p.touch ? HOLD_SLOP_PX : MOUSE_SLOP_PX);
            if (!far) return;
            // A finger that moves before the hold is finished is scrolling the grid;
            // a mouse that moves at all is dragging.
            if (p.touch) clearPending();
            else lift(e.clientX, e.clientY, true);
            return;
        }
        if (!drag) return;
        const target = targetAt(e.clientX, e.clientY);
        setDrag(d => (d ? {
            ...d,
            moved: true,
            startMin: timeAtDrag(e.clientX, d.grabOffsetPx),
            tableId: target ? target.tableId : d.tableId,
            tableNo: target ? target.tableNo : d.tableNo,
            rowTop: target ? target.rowTop : d.rowTop,
        } : d));
    };

    /** What the drop would change, or null when it would change nothing. */
    const dropChanges = (d: DragState): { time?: string; tableIds?: string[] } | null => {
        const changes: { time?: string; tableIds?: string[] } = {};
        const time = clock(d.startMin);
        if (time !== d.booking.time) changes.time = time;
        if (!d.timeOnly) {
            const held = d.booking.tableIds;
            if (d.tableId && !(held.length === 1 && held[0] === d.tableId)) {
                changes.tableIds = [d.tableId];
            } else if (!d.tableId && held.length > 0) {
                // Dropped in the "No table" lane: keep the booking, let the table go.
                changes.tableIds = [];
            }
        }
        return Object.keys(changes).length ? changes : null;
    };

    const blockPointerUp = async () => {
        const d = drag;
        clearPending();
        if (!d) return;
        dragged.current = true;
        window.setTimeout(() => { dragged.current = false; }, 0);
        setDrag(null);
        const changes = d.moved ? dropChanges(d) : null;
        if (!changes) return;
        const result = await onMove(d.booking, changes);
        if (!result.ok) setRefused({ booking: d.booking, changes, clashes: result.clashes });
    };

    const abortDrag = () => { clearPending(); setDrag(null); };

    useEffect(() => {
        if (!drag) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') abortDrag(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // Only re-bind when a drag starts or ends. abortDrag is stable for the life of one
        // drag, and listing it would tear the Escape handler down mid-drag on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drag]);

    /** Insist on a refused move, or drop it. */
    const insist = async () => {
        if (!refused) return;
        const result = await onMove(refused.booking, refused.changes, true);
        if (result.ok) setRefused(null);
    };

    const renderBlock = (r: Reservation, lane: number) => {
        const { startMin, endMin } = spanOf(r);
        const late = lateFor(r);
        const meta = RESERVATION_STATUS_META[r.status];
        const left = (startMin - span.startMin) * pxPerMin;
        const width = (endMin - startMin) * pxPerMin;
        const movable = canEdit && MOVABLE_STATUSES.includes(r.status);
        const classes = [
            'tline-block',
            `is-${r.status}`,
            late?.late ? 'is-overdue' : '',
            late?.held ? 'is-held' : '',
            movable && !assigning ? 'is-movable' : '',
            drag?.booking.id === r.id ? 'is-lifted' : '',
        ].filter(Boolean).join(' ');
        const joined = r.tableIds.length > 1;
        return (
            <button
                key={r.id}
                type='button'
                className={classes}
                style={{
                    ...laneBox(lane),
                    left: `${left}px`,
                    width: `${Math.max(width, 40)}px`,
                    // The turnaround is drawn past the end of the block, so a host can
                    // see why the next sitting cannot start the minute this one ends.
                    ['--tline-buffer' as any]: `${bufferMinutes * pxPerMin}px`,
                    ['--tline-accent' as any]: meta.accent,
                }}
                title={[
                    `${r.time}–${clock(endMin)} · ${guestLabel(r)} · ${r.partySize}`,
                    meta.label,
                    joined ? `Joined: ${r.tableIds.length} tables` : '',
                    late?.late ? `${late.lateBy}m late — nobody has seated them` : '',
                    late?.held ? `On their way — held ${late.heldFor}m more` : '',
                    movable ? 'Drag to move it' : '',
                ].filter(Boolean).join('\n')}
                onClick={(e) => {
                    e.stopPropagation();
                    // The drag ends in a click on the same element; opening the
                    // booking as well would undo the point of having dragged it.
                    if (dragged.current) return;
                    onOpenBooking(r);
                }}
                onPointerDown={(e) => blockPointerDown(e, r)}
                onPointerMove={blockPointerMove}
                onPointerUp={blockPointerUp}
                onPointerCancel={abortDrag}
                disabled={!!assigning}
            >
                <span className='tline-block__t'>{r.time}</span>
                <span className='tline-block__n'>{guestLabel(r)}</span>
                <span className='tline-block__p'>{icons.people}{r.partySize}</span>
            </button>
        );
    };

    const renderRow = (table: Table) => {
        const busy = resv.isToday ? occupancyFor(table) : null;
        const picked = assignPicks.includes(table.id);
        const poorFit = !!assigning && table.seats < assigning.partySize;
        const clash = !!assignSpan && (bookingsByTable[table.id] || []).some(r => {
            if (assigning && r.id === assigning.id) return false;
            const s = spanOf(r);
            return s.startMin < assignSpan.endMin + bufferMinutes
                && assignSpan.startMin < s.endMin + bufferMinutes;
        });
        const rowClasses = [
            'tline-row',
            !table.isActive ? 'is-off' : '',
            picked ? 'is-picked' : '',
            assigning && clash ? 'is-clash' : '',
            assigning && !clash && !poorFit ? 'is-fits' : '',
            poorFit ? 'is-poorfit' : '',
        ].filter(Boolean).join(' ');

        // The walk-in is packed alongside the bookings rather than drawn under
        // them: it holds the table just as firmly, so anything overlapping it has
        // to be pushed into its own sub-lane to stay legible.
        const { packed, lanes } = packLanes([
            ...(busy ? [{ startMin: busy.startMin, endMin: busy.endMin, busy }] : []),
            ...(bookingsByTable[table.id] || []).map(r => ({ ...spanOf(r), booking: r })),
        ] as { startMin: number; endMin: number; busy?: Occupancy; booking?: Reservation }[]);

        return (
            <div
                className={rowClasses}
                key={table.id}
                style={{ height: `calc(var(--tline-row-h) * ${lanes})` }}
                /* Read back by the drag to work out which table it is over: the rows
                 * vary in height, so hit-testing the DOM beats arithmetic. */
                data-table-id={table.id}
                data-table-no={table.tableNo}
                data-off={!table.isActive ? 'true' : undefined}
            >
                <div className='tline-row__head'>
                    <span className='tline-row__no'>{table.tableNo}</span>
                    <span className='tline-row__seats'>{icons.people}{table.seats}</span>
                    {picked && <span className='tline-row__pick' aria-hidden='true'>✓</span>}
                </div>
                <div
                    className='tline-row__lane'
                    style={{ width: `${laneWidth}px` }}
                    onClick={(e) => laneClick(e, table)}
                    role={assigning || canCreate ? 'button' : undefined}
                    aria-label={assigning
                        ? `Pick ${table.tableNo}`
                        : `Book ${table.tableNo}`}
                >
                    {assignSpan && (
                        <span
                            className='tline-ghost'
                            style={{
                                left: `${(assignSpan.startMin - span.startMin) * pxPerMin}px`,
                                width: `${(assignSpan.endMin - assignSpan.startMin) * pxPerMin}px`,
                            }}
                            aria-hidden='true'
                        />
                    )}

                    {packed.map(item => (item.booking
                        ? renderBlock(item.booking, item.lane)
                        : (
                            /* Sat without a booking behind it — a walk-in still owns the
                             * table, and a gap that isn't really a gap is worse than no
                             * grid at all. */
                            <span
                                key='busy'
                                className={`tline-busy${item.busy!.over ? ' is-over' : ''}`}
                                style={{
                                    ...laneBox(item.lane),
                                    left: `${(item.startMin - span.startMin) * pxPerMin}px`,
                                    width: `${Math.max((item.endMin - item.startMin) * pxPerMin, 40)}px`,
                                }}
                                title={`Walk-in · ${item.busy!.covers} covers · opened ${clock(item.startMin)}${item.busy!.over ? ' · past its turn' : ''}`}
                            >
                                <span className='tline-busy__label'>Walk-in{item.busy!.covers ? ` · ${item.busy!.covers}` : ''}</span>
                            </span>
                        )
                    ))}
                </div>
            </div>
        );
    };

    const refusedText = (() => {
        if (!refused) return '';
        const [first, ...rest] = refused.clashes;
        if (!first) return 'That move was refused.';
        const who = `${first.guestName || first.code} · ${first.partySize}`;
        return `${first.tableNo || 'That table'} is taken at ${first.time} by ${who}`
            + (rest.length ? ` and ${rest.length} more` : '');
    })();

    return (
        <div className={`tline${drag ? ' is-dragging' : ''}`}>
            <div className='tline__bar'>
                <div className='tline__date'>
                    <button
                        type='button'
                        className='resv-iconbtn'
                        onClick={() => resv.stepDate(-1)}
                        aria-label='Previous day'
                    >{icons.prev}</button>
                    <button
                        type='button'
                        className='tline__datebtn'
                        ref={dateBtnRef}
                        onClick={() => setJumperOpen(o => !o)}
                        aria-expanded={jumperOpen}
                        title='Pick a date'
                    >{dateLabel}</button>
                    <button
                        type='button'
                        className='resv-iconbtn'
                        onClick={() => resv.stepDate(1)}
                        aria-label='Next day'
                    >{icons.next}</button>
                    {jumperOpen && (
                        <DateJumper
                            value={resv.dateKey}
                            anchor={dateBtnRef}
                            onPick={resv.setDateKey}
                            onClose={() => setJumperOpen(false)}
                        />
                    )}
                </div>

                {!resv.isToday && (
                    <button type='button' className='tline__link' onClick={resv.goToday}>Today</button>
                )}
                {nowInSpan && (
                    <button type='button' className='tline__link' onClick={scrollToNow}>Now</button>
                )}

                <span className='tline__sum'>
                    {resv.kpis.bookings} booking{resv.kpis.bookings === 1 ? '' : 's'} · {resv.kpis.covers} covers
                </span>

                {elsewhere.count > 0 && (
                    <span className='tline__note' title='Their tables are on another floor'>
                        {elsewhere.count} on {elsewhere.floors || 'another floor'}
                    </span>
                )}

                {assigning && (
                    <span className='tline__note tline__note--assign'>
                        Pick a table for {assigning.time} · {assigning.partySize}
                    </span>
                )}

                {/* The numbers, not just the pixels: a drop is a decision about a time
                  * and a table, and neither should have to be read off the grid. */}
                {drag && (
                    <span className='tline__note tline__note--drag'>
                        {drag.booking.time} → {clock(drag.startMin)}
                        {drag.timeOnly
                            ? ' · joined, time only'
                            : drag.tableId ? ` · ${drag.tableNo}` : ' · no table'}
                    </span>
                )}
            </div>

            {refused && (
                <div className='tline__refuse' role='alert'>
                    <span className='tline__refuse-txt'>{refusedText}</span>
                    <button type='button' className='tline__link' onClick={insist}>Move anyway</button>
                    <button
                        type='button'
                        className='tline__link'
                        onClick={() => setRefused(null)}
                    >Leave it</button>
                </div>
            )}

            <div className='tline__viewport' ref={viewportRef}>
                <div
                    className='tline__grid'
                    style={{ ['--tline-hour-w' as any]: `${hourWidth}px` }}
                >
                    <div className='tline__head'>
                        <div className='tline__corner' ref={cornerRef}>Table</div>
                        <div className='tline__hours' style={{ width: `${laneWidth}px` }}>
                            {Array.from({ length: hours }, (_, i) => (
                                <span className='tline__hour' key={i}>
                                    {clock(span.startMin + i * 60)}
                                </span>
                            ))}
                        </div>
                        {/* The clock reads in the header rather than on the line, which has
                          * to stay under the sticky table column as the grid scrolls. */}
                        {nowInSpan && (
                            <span className='tline__nowpin' style={{ left: nowLeft }}>{clock(nowMin)}</span>
                        )}
                    </div>

                    <div className='tline__rows' ref={rowsRef}>
                        {unassignedRow.packed.length > 0 && (
                            <div
                                className='tline-row tline-row--unassigned'
                                style={{ height: `calc(var(--tline-row-h) * ${unassignedRow.lanes})` }}
                            >
                                <div className='tline-row__head'>
                                    <span className='tline-row__no'>No table</span>
                                    <span className='tline-row__seats'>{unassignedRow.packed.length}</span>
                                </div>
                                <div className='tline-row__lane' style={{ width: `${laneWidth}px` }}>
                                    {unassignedRow.packed.map(({ booking: r, lane, startMin, endMin }) => (
                                        <button
                                            key={r.id}
                                            type='button'
                                            className={`tline-block is-unassigned${
                                                canEdit && MOVABLE_STATUSES.includes(r.status) && !assigning ? ' is-movable' : ''
                                            }${drag?.booking.id === r.id ? ' is-lifted' : ''}`}
                                            style={{
                                                ...laneBox(lane),
                                                left: `${(startMin - span.startMin) * pxPerMin}px`,
                                                width: `${Math.max((endMin - startMin) * pxPerMin, 40)}px`,
                                                ['--tline-accent' as any]: RESERVATION_STATUS_META[r.status].accent,
                                            }}
                                            title={`${r.time} · ${guestLabel(r)} · ${r.partySize} — tap to give it a table, or drag it onto one`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (dragged.current) return;
                                                onAssign(r);
                                            }}
                                            onPointerDown={(e) => blockPointerDown(e, r)}
                                            onPointerMove={blockPointerMove}
                                            onPointerUp={blockPointerUp}
                                            onPointerCancel={abortDrag}
                                        >
                                            <span className='tline-block__t'>{r.time}</span>
                                            <span className='tline-block__n'>{guestLabel(r)}</span>
                                            <span className='tline-block__p'>{icons.people}{r.partySize}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {rows.map(renderRow)}

                        {rows.length === 0 && (
                            <div className='tline__empty'>No tables on this floor yet.</div>
                        )}

                        {nowInSpan && <div className='tline__now' style={{ left: nowLeft }} />}

                        {/* Where it would land. Drawn over the rows rather than inside
                          * one, so it can sit on top of whatever it would collide with
                          * — seeing the overlap is the point. */}
                        {drag?.moved && (
                            <div
                                className={`tline-preview${dropChanges(drag) ? '' : ' is-nochange'}`}
                                style={{
                                    top: `${drag.rowTop + 3}px`,
                                    left: `calc(var(--tline-gutter-w) + ${(drag.startMin - span.startMin) * pxPerMin}px)`,
                                    width: `${Math.max(
                                        (spanOf(drag.booking).endMin - spanOf(drag.booking).startMin) * pxPerMin,
                                        40,
                                    )}px`,
                                }}
                                aria-hidden='true'
                            >
                                <span className='tline-block__t'>{clock(drag.startMin)}</span>
                                <span className='tline-block__n'>{guestLabel(drag.booking)}</span>
                                <span className='tline-block__p'>{icons.people}{drag.booking.partySize}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceTimeline;
